// Paginates the currently visible statement rows and updates after filter changes.
class StatementsPagination {
  // Collects the pagination elements, rows and configuration.
  constructor(options = {}) {
    this.element = options.element;
    this.statements = this.element.closest('.org-statements');
    this.pages = this.element.querySelector('.org-statements-pagination__pages');
    this.rows = this.statements ?
      Array.from(this.statements.querySelectorAll('tbody tr[data-plan-ids]')) :
      [];
    this.itemsPerPage = parseInt(this.element.dataset.itemsPerPage, 10) || 6;
    this.visiblePageButtons = Math.max(
      parseInt(this.element.dataset.visiblePageButtons, 10) || 5,
      3,
    );
    this.currentPage = 1;
  }

  // Initializes pagination and listens for statement filter changes.
  init() {
    if (!this.statements || !this.pages || this.rows.length === 0) {
      return;
    }

    this.statements.addEventListener('statements:filter-change', () => {
      this.currentPage = 1;
      this.update();
    });

    this.update();
  }

  // Returns rows that are not hidden by the statement filters.
  getFilteredRows() {
    return this.rows.filter(row => (
      !row.classList.contains('is-hidden-by-plan-filter') &&
      !row.classList.contains('is-hidden-by-year-filter')
    ));
  }

  // Calculates the number of pages for the filtered rows.
  getTotalPages(filteredRows) {
    return Math.ceil(filteredRows.length / this.itemsPerPage);
  }

  // Updates row visibility and renders the current pagination state.
  update() {
    const filteredRows = this.getFilteredRows();
    const totalPages = this.getTotalPages(filteredRows);

    this.currentPage = Math.min(this.currentPage, Math.max(totalPages, 1));
    this.updateRowVisibility(filteredRows);
    this.render(totalPages);
  }

  // Shows only the filtered rows that belong to the current page.
  updateRowVisibility(filteredRows) {
    const firstRowIndex = (this.currentPage - 1) * this.itemsPerPage;
    const visibleRows = filteredRows.slice(firstRowIndex, firstRowIndex + this.itemsPerPage);

    this.rows.forEach((row) => {
      row.classList.toggle(
        'is-hidden-by-pagination',
        filteredRows.indexOf(row) !== -1 && visibleRows.indexOf(row) === -1,
      );
    });
  }

  // Rebuilds the page navigation and hides it when unnecessary.
  render(totalPages) {
    this.pages.innerHTML = '';
    this.element.hidden = totalPages <= 1;

    if (totalPages <= 1) {
      return;
    }

    this.getPaginationItems(totalPages).forEach((item) => {
      if (item === null) {
        this.pages.appendChild(this.createEllipsis());
      } else {
        this.pages.appendChild(this.createPageButton(item));
      }
    });
  }

  // Creates the compact list of page numbers and ellipses.
  getPaginationItems(totalPages) {
    if (totalPages <= this.visiblePageButtons) {
      return Array.from({ length: totalPages }, (value, index) => index + 1);
    }

    const middleButtonsCount = this.visiblePageButtons - 2;
    const halfMiddleButtons = Math.floor(middleButtonsCount / 2);
    let firstMiddlePage = this.currentPage - halfMiddleButtons;
    let lastMiddlePage = firstMiddlePage + middleButtonsCount - 1;

    if (firstMiddlePage < 2) {
      firstMiddlePage = 2;
      lastMiddlePage = firstMiddlePage + middleButtonsCount - 1;
    }

    if (lastMiddlePage > totalPages - 1) {
      lastMiddlePage = totalPages - 1;
      firstMiddlePage = lastMiddlePage - middleButtonsCount + 1;
    }

    const items = [1];

    if (firstMiddlePage > 2) {
      items.push(null);
    }

    for (let page = firstMiddlePage; page <= lastMiddlePage; page += 1) {
      items.push(page);
    }

    if (lastMiddlePage < totalPages - 1) {
      items.push(null);
    }

    items.push(totalPages);
    return items;
  }

  // Creates an interactive button for a page number.
  createPageButton(page) {
    const button = document.createElement('button');
    button.classList.add('org-statements-pagination__button');
    button.type = 'button';
    button.textContent = page;
    button.setAttribute('aria-label', `Seite ${page}`);

    if (page === this.currentPage) {
      button.setAttribute('aria-current', 'page');
    }

    button.addEventListener('click', () => {
      this.currentPage = page;
      this.update();
    });

    return button;
  }

  // Creates a non-interactive ellipsis between page ranges.
  createEllipsis() {
    const ellipsis = document.createElement('span');
    ellipsis.classList.add('org-statements-pagination__ellipsis');
    ellipsis.setAttribute('aria-hidden', 'true');
    ellipsis.textContent = '...';
    return ellipsis;
  }
}

export default StatementsPagination;
